<script>
  import './glass.css';
  export let beforeImage;
  export let afterImage;
  export let beforeLabel = 'Before';
  export let afterLabel = 'After';
  export let className = '';

  let sliderPosition = 50;
  let isDragging = false;
  let containerEl;

  function handleMouseDown() {
    isDragging = true;
  }

  function handleMouseUp() {
    isDragging = false;
  }

  function updatePositionFromClientX(clientX) {
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    sliderPosition = percentage;
  }

  function handleMouseMove(event) {
    if (!isDragging) return;
    updatePositionFromClientX(event.clientX);
  }

  function handleTouchMove(event) {
    if (!isDragging) return;
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    updatePositionFromClientX(touch.clientX);
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      sliderPosition = Math.max(0, sliderPosition - 2);
    } else if (event.key === 'ArrowRight') {
      sliderPosition = Math.min(100, sliderPosition + 2);
    }
  }
</script>

<div
  bind:this={containerEl}
  role="slider"
  tabindex="0"
  aria-valuemin="0"
  aria-valuemax="100"
  aria-valuenow={Math.round(sliderPosition)}
  aria-label="Before and after comparison slider"
  class={"liquid-glass-slider relative w-full h-64 sm:h-80 md:h-96 lg:h-[28rem] xl:h-[32rem] overflow-hidden rounded-2xl select-none " + className}
  on:mousemove={handleMouseMove}
  on:mouseup={handleMouseUp}
  on:mouseleave={handleMouseUp}
  on:touchmove={handleTouchMove}
  on:touchend={handleMouseUp}
  on:keydown={handleKeyDown}
>
  <!-- Liquid glass border effect -->
  <div class="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/30 via-transparent to-blue-500/20 p-0.5">
    <div class="w-full h-full bg-black rounded-2xl overflow-hidden">
      
      <!-- Before image with glass overlay -->
      <div class="absolute inset-0">
        <img src={beforeImage} alt={beforeLabel} class="w-full h-full object-cover" draggable={false} />
        <!-- Glass morphism label -->
        <div class="glass-label absolute top-4 left-4 bg-black/40 backdrop-blur-md border border-white/20 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
          <span class="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">{beforeLabel}</span>
        </div>
      </div>

      <!-- After image with glass overlay -->
      <div
        class="absolute inset-0 transition-all duration-150 ease-out"
        style={`clip-path: polygon(${sliderPosition}% 0%, 100% 0%, 100% 100%, ${sliderPosition}% 100%)`}
      >
        <img src={afterImage} alt={afterLabel} class="w-full h-full object-cover" draggable={false} />
        <!-- Glass morphism label -->
        <div class="glass-label absolute top-4 right-4 bg-black/40 backdrop-blur-md border border-white/20 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
          <span class="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">{afterLabel}</span>
        </div>
      </div>

      <!-- Liquid glass slider line -->
      <div
        class="liquid-line absolute top-0 bottom-0 w-1 z-20 transition-all duration-150 ease-out"
        style={`left: ${sliderPosition}%; transform: translateX(-50%);`}
      >
        <!-- Main slider line with gradient -->
        <div class="w-full h-full bg-gradient-to-b from-blue-400 via-white to-blue-500 shadow-2xl relative overflow-hidden">
          <!-- Animated liquid flow effect -->
          <div class="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-transparent animate-liquid-flow"></div>
        </div>
      </div>

      <!-- Liquid glass slider handle -->
      <div
        role="button"
        aria-label="Move slider handle"
        tabindex="0"
        class="liquid-handle absolute top-1/2 z-30 transform -translate-y-1/2 transition-all duration-150 ease-out cursor-ew-resize group"
        style={`left: ${sliderPosition}%; transform: translate(-50%, -50%);`}
        on:mousedown={handleMouseDown}
        on:touchstart={handleMouseDown}
      >
        <!-- Outer glow ring -->
        <div class="absolute inset-0 w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-30 animate-pulse-ring scale-150"></div>
        
        <!-- Main handle -->
        <div class="liquid-handle-main relative w-10 h-10 bg-white/90 backdrop-blur-lg border-2 border-white/50 rounded-full shadow-2xl flex items-center justify-center transform transition-all duration-200 group-hover:scale-125 group-hover:shadow-3xl">
          <!-- Liquid gradient overlay -->
          <div class="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/20 via-transparent to-purple-500/20"></div>
          
          <!-- Handle icon -->
          <div class="flex space-x-0.5 z-10">
            <div class="w-0.5 h-5 bg-gradient-to-b from-blue-600 to-purple-600 rounded-full animate-liquid-pulse"></div>
            <div class="w-0.5 h-5 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full animate-liquid-pulse" style="animation-delay: 0.2s;"></div>
          </div>

          <!-- Interactive ripple effect -->
          <div class="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/0 via-blue-500/30 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-ripple"></div>
        </div>
      </div>

      <!-- Liquid glass instructions -->
      <div class="glass-instructions absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/40 backdrop-blur-md border border-white/20 text-white px-6 py-3 rounded-full text-sm font-medium shadow-xl">
        <div class="flex items-center space-x-2">
          <div class="w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full animate-pulse-subtle"></div>
          <span class="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">Drag or use ← → keys to compare</span>
        </div>
      </div>

      <!-- Liquid shimmer overlay -->
      <div class="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
        <div class="liquid-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 animate-shimmer-slow"></div>
      </div>
    </div>
  </div>
</div>